import { useDisclosure } from "@mantine/hooks";
import { IconPencil } from "@tabler/icons-react";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useIsFeatureEnabled } from "#app/featureToggle";
import { colors } from "#app/theme/v1/colors";
import { useUpdateLayout } from "#modules/layouts/view/useUpdateLayout";
import { LayoutSidePanel } from "#modules/layoutsV2/view/LayoutSidePanel";
import { usePart } from "#modules/parts/view/usePart";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import LayoutModal from "#modules/quoteRequestLines/view/LayoutModal";
import { MaterialLayoutModal } from "#modules/quoteRequestLines/view/MaterialLayoutModal";
import { useQuoteRequestLineOverthickness } from "#modules/quoteRequestLines/view/useQuoteRequestLineOverthickness";
import { MaterialDimensions } from "#modules/quoteRequestLines/views/DetailsPanel/Material/MaterialDimensions";
import { MaterialLayouts } from "#modules/quoteRequestLines/views/DetailsPanel/Material/MaterialLayouts";
import MaterialPrice from "#modules/quoteRequestLines/views/DetailsPanel/Material/MaterialPrice";
import { MaterialWeight } from "#modules/quoteRequestLines/views/DetailsPanel/Material/MaterialWeight";
import { Icon } from "#shared/icons/Icon";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Alert } from "#shared/view/components/Alert";
import { Box } from "#shared/view/components/Box";
import { Flex } from "#shared/view/components/Flex";
import { Group } from "#shared/view/components/Group";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { Checkbox } from "#shared/view/designSystem/Checkbox";
import { Skeleton } from "#shared/view/designSystem/Skeleton";
import { Tooltip } from "#shared/view/designSystem/Tooltip";
import { Features } from "#types/FeatureToggleTypes";
import {
  NestingModeType,
  PricingModeSchema,
  PricingModeType,
  getDefaultUseNesting,
  getOverThicknessFromPartType,
  isJobProcessing,
} from "shared";

import { useAlertChip } from "../../useAlertChip";
import { NestedMaterialInformations } from "./NestedMaterialInformations";
import { NoMaterialLayout } from "./NoMaterialLayout";

export const MaterialCost = ({
  quoteRequestLine,
  nestingMode,
  companyPricingMode,
  hasChildren,
  isChild,
  openOptimizationDetail,
}: {
  quoteRequestLine: Omit<QuoteRequestLineDto, "layout"> & {
    layout: NonNullable<QuoteRequestLineDto["layout"]>;
  };
  nestingMode: NestingModeType;
  companyPricingMode: PricingModeType;
  hasChildren: boolean;
  isChild: boolean;
  openOptimizationDetail?: (optimizationId: string) => void;
}) => {
  const { control, getValues } = useFormContext<QuoteRequestLineFormData>();
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const layout = quoteRequestLine.layout;
  const multiPartNestingOptimizationId = quoteRequestLine.multiPartNestingOptimizationId;
  const isTrueShapeNestingEnabled = useIsFeatureEnabled(Features.TRUE_SHAPE_NESTING);
  const isInMultiPartNesting = isTrueShapeNestingEnabled && !!multiPartNestingOptimizationId;

  const useNestingDefault = useMemo(() => {
    if (quoteRequestLine.layout.useNesting === null) {
      return getDefaultUseNesting({
        partType: quoteRequestLine.part.type,
        nestingMode,
        pricingMode: layout.materialLayout?.pricingMode ?? companyPricingMode,
      });
    }
    return quoteRequestLine.layout.useNesting;
  }, [
    quoteRequestLine.layout.useNesting,
    layout.materialLayout?.pricingMode,
    nestingMode,
    quoteRequestLine.part.type,
    companyPricingMode,
  ]);

  const [useNesting, setUseNesting] = useState<boolean>(useNestingDefault);

  useEffect(() => {
    setUseNesting(useNestingDefault);
  }, [useNestingDefault]);

  const [
    isMaterialLayoutModalOpened,
    { open: openMaterialLayoutModal, close: closeMaterialLayoutModal },
  ] = useDisclosure(false);
  const [isLayoutModalOpened, { open: openLayoutModal, close: closeLayoutModal }] =
    useDisclosure(false);
  const materialGrade = useWatch({ control, name: "materialGrade" });

  const materialLayout = layout.materialLayout;
  const quoteRequestLineId = useWatch({ control, name: "quoteRequestLineId" });
  const materialNameWatched = useWatch({ control, name: "materialName" });
  const materialName =
    materialNameWatched || (materialGrade ? t(`qrl.materialGrades.${materialGrade}`) : "-");
  const useVolumetricPricingMode =
    materialLayout?.pricingMode === PricingModeSchema.enum.VOLUMETRIC;

  const { part } = usePart(getValues("partId"));
  const { overthickness, isLoading: isLoadingOverthickness } =
    useQuoteRequestLineOverthickness(quoteRequestLineId);

  const { updateLayout, isPending: isUpdatingLayout } = useUpdateLayout(quoteRequestLine.id);
  const isNestingInProgress =
    isTrueShapeNestingEnabled &&
    !!quoteRequestLine.nestingStatus &&
    isJobProcessing(quoteRequestLine.nestingStatus);

  const cancel = () => {
    closeMaterialLayoutModal();
  };

  const onLayoutChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (!layout.materialLayout) {
        throw new Error(
          "Layout or material layout not found. Impossible to update useNesting checkbox.",
        );
      }
      const newUseNesting = e.target.checked;
      setUseNesting(newUseNesting);

      analytics.capture(AnalyticsEvent.DetailsPanelLayoutEdited, {
        quote_request_line_id: quoteRequestLine.id,
        field: "useNesting",
      });

      updateLayout({
        id: layout.id,
        payload: { useNesting: newUseNesting, quoteRequestLineId: quoteRequestLine.id },
      });
    },
    [quoteRequestLine, updateLayout, layout, analytics],
  );

  const { alerts } = useAlertChip(quoteRequestLine);

  const materialCostAlert = alerts.find((alert) => alert.processStepId === "material_cost_alert");

  if (isLoadingOverthickness) {
    return (
      <Stack gap="xs">
        <MaterialLayoutsSkeleton />

        <Group gap={"xs"}>
          <Text>
            {t("qrl.materialSpecification")} : <b>{materialName}</b>
          </Text>
        </Group>

        <Flex align={"center"} gap={"xs"}>
          <Skeleton height={36} width={225} />
          <Skeleton height={36} width={225} />
        </Flex>
      </Stack>
    );
  }

  if (!overthickness) {
    return (
      <Stack gap="xs">
        <Alert title={t("qrl.error.overthicknessDataMissing")} color="red">
          {t("qrl.error.overthicknessDataMissingMessage")}
        </Alert>
      </Stack>
    );
  }

  if (!part) {
    return (
      <Stack gap="xs">
        <Alert title={t("qrl.error.partDataMissing")} color="red">
          {t("qrl.error.partDataMissingMessage")}
        </Alert>
      </Stack>
    );
  }

  if (!materialGrade) {
    return (
      <Stack gap="xs">
        <Alert title={t("qrl.error.materialGradeMissing")} color="red">
          {t("qrl.error.materialGradeMissingMessage")}
        </Alert>
      </Stack>
    );
  }

  const overthicknessForPart = getOverThicknessFromPartType(part.type, overthickness);

  const partWithOverthicknessDimensions = {
    ...part,
    height: (part.height ?? 0) + overthicknessForPart.height,
    width: (part.width ?? 0) + overthicknessForPart.width,
    length: (part.length ?? 0) + overthicknessForPart.length,
  };

  return (
    <Stack gap="xs">
      <Stack gap={0}>
        {isNestingInProgress ||
        (!!quoteRequestLine.predictionStatus &&
          isJobProcessing(quoteRequestLine.predictionStatus)) ? (
          <MaterialLayoutsSkeleton />
        ) : (
          materialLayout &&
          !useVolumetricPricingMode &&
          !isInMultiPartNesting && (
            <Flex justify="space-between">
              <Stack gap="xs">
                <Checkbox
                  label={t("qrl.material.useNesting")}
                  size="xs"
                  onChange={onLayoutChange}
                  checked={useNesting}
                  styles={{
                    input: { cursor: "pointer" },
                  }}
                />
                {useNesting &&
                  (isUpdatingLayout ? (
                    <Skeleton height={24} width={250} />
                  ) : (
                    <MaterialLayouts
                      layout={layout}
                      materialLayout={materialLayout}
                      partWithOverthicknessDimensions={partWithOverthicknessDimensions}
                    />
                  ))}
              </Stack>
              {useNesting && (
                <Button onClick={openLayoutModal} variant="light">
                  {t("qrl.material.editNesting")}
                </Button>
              )}
            </Flex>
          )
        )}
      </Stack>

      <Flex justify="space-between" align="flex-start" gap="xs">
        <Stack gap={"xxxs"}>
          <Text>
            {t("qrl.materialSpecification")} : <b>{materialName}</b>
          </Text>
          <NestedMaterialInformations
            part={part}
            materialLayout={materialLayout}
            useNesting={useNesting}
            isInMultiPartNesting={isInMultiPartNesting}
            overthickness={overthickness}
          />
        </Stack>
        {isInMultiPartNesting && multiPartNestingOptimizationId && (
          <Button
            onClick={() => openOptimizationDetail?.(multiPartNestingOptimizationId)}
            variant="light"
          >
            {t("qrl.material.viewComplexNesting")}
          </Button>
        )}
      </Flex>

      <Flex align={"end"} gap={"xs"}>
        {!useVolumetricPricingMode && materialLayout && (
          <MaterialDimensions
            materialLayout={materialLayout}
            openMaterialLayoutModal={openMaterialLayoutModal}
            materialGrade={materialGrade}
            materialName={materialName}
            quoteRequestLineId={quoteRequestLineId}
            partType={part.type}
            partSubType={part.subType}
            partDimensionsWithOverthickness={partWithOverthicknessDimensions}
          />
        )}

        {!materialLayout && (
          <NoMaterialLayout
            openMaterialLayoutModal={openMaterialLayoutModal}
            materialGrade={materialGrade}
            part={part}
            quoteRequestLine={quoteRequestLine}
            partDimensionsWithOverthickness={partWithOverthicknessDimensions}
          />
        )}

        {materialLayout && (
          <MaterialPrice
            materialGrade={materialGrade}
            part={part}
            materialLayout={materialLayout}
          />
        )}

        {materialLayout && (
          <ActionIcon
            variant="outline"
            color="gray"
            size={36}
            onClick={openMaterialLayoutModal}
            aria-label={t("material.updateMaterialLayoutModalTitle")}
            styles={{
              root: {
                borderColor: colors.gray[3],
              },
            }}
          >
            <IconPencil size={16} />
          </ActionIcon>
        )}

        {materialLayout && materialCostAlert && (
          <Box pt="md">
            <Tooltip
              multiline
              withArrow
              label={
                <Text>
                  {t(
                    `alert.${materialCostAlert.message}`,
                    materialCostAlert.messageParameters || undefined,
                  )}
                </Text>
              }
            >
              <Icon name="alertTriangle" size={18} color={colors.orange[7]} />
            </Tooltip>
          </Box>
        )}
      </Flex>

      {materialLayout && (
        <Stack gap="xxs">
          <MaterialWeight
            volume={materialLayout.volume}
            density={materialLayout.density}
            part={part}
            quoteRequestLineId={quoteRequestLine.id}
          />
        </Stack>
      )}

      {isMaterialLayoutModalOpened && (
        <MaterialLayoutModal
          hasChildren={hasChildren}
          isOpened={isMaterialLayoutModalOpened}
          close={closeMaterialLayoutModal}
          cancel={cancel}
          quoteRequestLineId={quoteRequestLineId}
          materialGrade={materialGrade}
          partType={part.type}
          partSubType={part.subType}
          overthickness={overthickness}
        />
      )}

      {isTrueShapeNestingEnabled && (
        <LayoutSidePanel
          opened={isLayoutModalOpened}
          onClose={closeLayoutModal}
          quoteRequestLine={quoteRequestLine}
          materialGrade={materialGrade}
          partDimensionsWithOverthickness={partWithOverthicknessDimensions}
        />
      )}

      {!isTrueShapeNestingEnabled && (
        <LayoutModal
          onClose={closeLayoutModal}
          show={isLayoutModalOpened}
          quoteRequestLine={quoteRequestLine}
          handleSetCurrentLayoutNesting={() => {}}
          currentLayoutNesting={null}
          hasChildren={hasChildren}
          isChild={isChild}
        />
      )}
    </Stack>
  );
};

const MaterialLayoutsSkeleton = () => {
  return (
    <Stack gap="xxxs" data-testid="material-layouts-skeleton">
      <Skeleton height={12} width={250} />
      <Skeleton height={12} width={150} />
      <Skeleton height={12} width={250} />
    </Stack>
  );
};
